import React, { SyntheticEvent, useEffect } from "react";
import { Button, Dropdown, Form, FormField, FormGroup, Input, TextArea } from "semantic-ui-react";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { DateTextBox } from "react-lib/apps/common";

// Interface
import { BTN_ACTS, PickedState, SetProp } from "./sequence/IssueList";

import { CreateIssueType } from "./sequence/RedmineAPIController";

type PanelNewIssueProps = {
  isEdit: boolean;
  // func
  onChange: (e: SyntheticEvent | null, data: Record<string, any>) => void;
  onCreate: () => void;
  onEdit: () => void;
  onClose: () => void;
  // History Modal
  onOpenModal: () => void;
  // data
  data: CreateIssueType;
  currentDivision: string;
  // options
  projectOptions: OptionsType;
  trackerOptions: OptionsType;
  statusOptions: OptionsType;
  priorityOptions: OptionsType;
  userOptions: OptionsType;
  divisionOptions: OptionsType;
  assignedDivisionOptions: OptionsType;
  // common
  setProp: SetProp;
} & Pick<PickedState, "buttonLoadCheck" | "errorMessage" | "successMessage">;

type OptionsType = Partial<{
  key: number | string;
  value: number | string;
  text: string;
}>[];

const PanelNewIssue = (props: PanelNewIssueProps) => {
  const { isEdit, data, currentDivision } = props;

  // set default division if exist
  useEffect(() => {
    if (!data.project_id || !currentDivision) return;

    const divisionExists = props.divisionOptions.some(
      (opt) => opt.value === currentDivision || opt.text === currentDivision
    );

    const currentValue =  getCustomFieldValue(data.custom_fields || [], "Division");

    if (divisionExists && currentValue === "") {
      props.onChange(null, { name: "division", value: currentDivision });
    }
  }, [data.project_id]);

  const getCustomFieldValue = (
    fields: Partial<{ name: string; value: any }>[],
    fieldName: string
  ) => {
    return fields.find((f) => f.name === fieldName)?.value || "";
  };

  return (
    <Form style={{ margin: "1rem" }}>
      <FormGroup>
        <FormField inline width={16} style={{ display: "flex", justifyContent: "space-between" }}>
          <label style={{ fontSize: "18px", color: "#4287f5" }}>
            {isEdit ? `Issue #${data.id}` : `New Issue`}
          </label>
          {isEdit && <Button icon="history" onClick={props.onOpenModal} />}
        </FormField>
      </FormGroup>
      <FormGroup inline>
        <FormField inline width={6}>
          <label>
            Project<span style={{ color: "red" }}>*</span> :
          </label>
          <div style={{ width: "50%" }}>
            <Dropdown
              fluid
              selection
              name="project_id"
              value={data.project_id || undefined}
              onChange={props.onChange}
              options={props.projectOptions}
            />
          </div>
        </FormField>
      </FormGroup>
      <FormGroup inline>
        <FormField inline width={6}>
          <label>
            Tracker<span style={{ color: "red" }}>*</span> :
          </label>
          <div style={{ width: "50%" }}>
            <Dropdown
              fluid
              selection
              name="tracker_id"
              value={data.tracker_id || undefined}
              onChange={props.onChange}
              options={props.trackerOptions}
            />
          </div>
        </FormField>
        <FormField inline width={6}>
          <label>
            Reporting Division<span style={{ color: "red" }}>*</span> :
          </label>
          <div style={{ width: "50%" }}>
            <Dropdown
              fluid
              selection
              name="division"
              value={getCustomFieldValue(data.custom_fields || [], "Division")}
              onChange={props.onChange}
              options={props.divisionOptions}
            />
          </div>
        </FormField>
      </FormGroup>
      <FormGroup inline>
        <FormField inline width={12}>
          <label>
            Subject<span style={{ color: "red" }}>*</span> :
          </label>
          <div style={{ width: "76%" }}>
            <Input fluid name="subject" value={data.subject || ""} onChange={props.onChange} />
          </div>
        </FormField>
      </FormGroup>
      <FormGroup>
        <FormField width={14}>
          <div style={{ width: "100%" }}>
            <label style={{ fontWeight: "bold" }}>Description :</label>
            <TextArea
              rows={5}
              name="description"
              value={data.description || ""}
              onChange={props.onChange}
            />
          </div>
        </FormField>
      </FormGroup>
      <FormGroup inline>
        <FormField inline width={6}>
          <label>
            Status<span style={{ color: "red" }}>*</span> :
          </label>
          <div style={{ width: "50%" }}>
            <Dropdown
              fluid
              selection
              name="status_id"
              value={data.status_id || undefined}
              onChange={props.onChange}
              options={props.statusOptions}
            />
          </div>
        </FormField>
        <FormField inline width={6}>
          <label>Start date :</label>
          <div style={{ width: "50%" }}>
            <DateTextBox
              value={data.start_date || ""}
              onChange={(value: any) => props.onChange(null, { name: "start_date", value })}
            />
          </div>
        </FormField>
      </FormGroup>
      <FormGroup inline>
        <FormField inline width={6}>
          <label>
            Priority<span style={{ color: "red" }}>*</span> :
          </label>
          <div style={{ width: "50%" }}>
            <Dropdown
              fluid
              selection
              name="priority_id"
              value={data.priority_id || undefined}
              onChange={props.onChange}
              options={props.priorityOptions}
            />
          </div>
        </FormField>
        <FormField inline width={6}>
          <label>Due date :</label>
          <div style={{ width: "50%" }}>
            <DateTextBox
              value={data.due_date || ""}
              onChange={(value: any) => props.onChange(null, { name: "due_date", value })}
            />
          </div>
        </FormField>
      </FormGroup>
      <FormGroup inline>
        <FormField inline width={6}>
          <label>Assign to :</label>
          <div style={{ width: "50%" }}>
            <Dropdown
              fluid
              selection
              clearable
              name="assigned_to_id"
              value={data.assigned_to_id || undefined}
              onChange={props.onChange}
              options={props.userOptions}
            />
          </div>
        </FormField>
        <FormField inline width={6}>
          <label>Assigned Division :</label>
          <div style={{ width: "50%" }}>
            <Dropdown
              fluid
              selection
              clearable
              name="assigned_division"
              value={getCustomFieldValue(data.custom_fields || [], "Assigned Division")}
              onChange={props.onChange}
              options={props.assignedDivisionOptions}
            />
          </div>
        </FormField>
      </FormGroup>
      <FormGroup>
        <FormField width={14}>
          <div style={{ width: "100%" }}>
            <label style={{ fontWeight: "bold" }}>Causes and Solutions :</label>
            <TextArea
              rows={5}
              name="causes_and_solutions"
              value={getCustomFieldValue(data.custom_fields || [], "Causes and Solutions")}
              onChange={props.onChange}
            />
          </div>
        </FormField>
      </FormGroup>
      {isEdit && (
        <FormGroup>
          <FormField width={14}>
            <div style={{ width: "100%" }}>
              <label style={{ fontWeight: "bold" }}>Notes :</label>
              <TextArea
                rows={5}
                name="notes"
                value={data.notes || undefined}
                onChange={props.onChange}
              />
            </div>
          </FormField>
        </FormGroup>
      )}
      <FormGroup inline>
        <FormField width={16} style={{ justifyContent: "flex-end" }}>
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={isEdit ? props.onEdit : props.onCreate}
            // data
            paramKey={isEdit ? BTN_ACTS.EDIT : BTN_ACTS.CREATE}
            buttonLoadCheck={props.buttonLoadCheck?.[isEdit ? BTN_ACTS.EDIT : BTN_ACTS.CREATE]}
            // config
            name={isEdit ? "EDIT" : "CREATE"}
            title={isEdit ? "EDIT" : "CREATE"}
            color={isEdit ? "yellow" : "green"}
          />
          <Button children="CLOSE" color="red" onClick={props.onClose} />
        </FormField>
      </FormGroup>
    </Form>
  );
};

export default PanelNewIssue;
